type AutomatedBrowserSignals = {
  hasHeadlessChromeUserAgent: boolean;
  hasNoPlugins: boolean;
  isChromeUserAgentWithoutWindowChrome: boolean;
  pluginCount: number;
  webdriver: boolean;
};

type WindowWithChrome = Window & {
  chrome?: unknown;
};

const AUTOMATED_BROWSER_HEURISTIC_VERSION = 'v1';

const getBrowserSignals = (): AutomatedBrowserSignals => {
  if (typeof window === 'undefined') {
    return {
      hasHeadlessChromeUserAgent: false,
      hasNoPlugins: false,
      isChromeUserAgentWithoutWindowChrome: false,
      pluginCount: 0,
      webdriver: false,
    };
  }

  const userAgent = navigator.userAgent || '';
  const pluginCount = navigator.plugins?.length ?? 0;
  const hasChromeUserAgent = /\bChrome\//i.test(userAgent);
  const hasWindowChrome = typeof (window as WindowWithChrome).chrome !== 'undefined';

  return {
    hasHeadlessChromeUserAgent: /HeadlessChrome/i.test(userAgent),
    hasNoPlugins: pluginCount === 0,
    isChromeUserAgentWithoutWindowChrome: hasChromeUserAgent && !hasWindowChrome,
    pluginCount,
    webdriver: navigator.webdriver === true,
  };
};

const getTriggeredSignals = (signals: AutomatedBrowserSignals): string[] =>
  [
    signals.webdriver && 'webdriver',
    signals.hasHeadlessChromeUserAgent && 'headless_chrome_user_agent',
    signals.isChromeUserAgentWithoutWindowChrome && 'chrome_user_agent_without_window_chrome',
    signals.hasNoPlugins && 'no_plugins',
  ].filter((signal): signal is string => Boolean(signal));

export const isLikelyAutomatedBrowser = (): boolean => {
  const signals = getBrowserSignals();

  if (signals.webdriver || signals.hasHeadlessChromeUserAgent) {
    return true;
  }

  return signals.isChromeUserAgentWithoutWindowChrome && signals.hasNoPlugins;
};

export const getLikelyAutomatedBrowserEventProperties = (
  routeName?: string
): Record<string, unknown> => {
  const signals = getBrowserSignals();
  const triggeredSignals = getTriggeredSignals(signals);

  return {
    current_path: window.location.pathname,
    detection_mode: 'observe_only',
    detection_source: 'client_browser_heuristics',
    heuristic_version: AUTOMATED_BROWSER_HEURISTIC_VERSION,
    is_likely_automated_browser: true,
    route_name: routeName || '',
    triggered_automation_signal_count: triggeredSignals.length,
    triggered_automation_signals: triggeredSignals,
    has_headless_chrome_user_agent: signals.hasHeadlessChromeUserAgent,
    has_no_plugins: signals.hasNoPlugins,
    is_chrome_user_agent_without_window_chrome: signals.isChromeUserAgentWithoutWindowChrome,
    plugin_count: signals.pluginCount,
    webdriver: signals.webdriver,
  };
};
